import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {Table} from "../components/ui/table";
import { useAuth } from "../components/features/AuthContext";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";
import { z } from "zod";
import { DataTable } from "../components/DataTable";

export const FilterationResultSchema = z.object({
  _id: z.string(),
  user_id: z.string(),
  symbol: z.string(),
  category: z.string(),
  filter_name: z.string().optional(),
  action: z.string().optional(),
  timing: z.string(), 
  price: z.number().optional(),
});

export type FilterationResultSchema = z.infer<typeof FilterationResultSchema>;

export default function FilterationResults() {
  const { user, currentTradingMode } = useAuth();
  const user_id = user?.userId
  const [page, setPage] = useState(0);
  const limit = 50;
  const headers = ["Filter_name", "Action", "Timing", "Price", "Symbol"];

  const {
    data: results,
    isLoading,
    isFetching,
  } = useQuery<FilterationResultSchema[]>({
    queryKey: ["api/filteration-results", user_id, currentTradingMode, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        category: currentTradingMode,
        skip: String(page * limit),
        limit: String(limit),
      });

      const res = await apiRequest<ApiResponse<FilterationResultSchema[]>>(
        "GET",
        `api/filteration-results/${user_id}?${params}`,
        undefined,
        undefined,
        "stocks"
      );
      return res.data;
    },
    enabled: !!user_id && !!currentTradingMode,
  });

  const {
    data: fileBlob,
    refetch: downloadFile,
    isFetching: isDownloading,
  } = useQuery({
    queryKey: ["download-filteration-results", user_id, currentTradingMode],
    queryFn: async () => {
      const params = new URLSearchParams({ category: currentTradingMode });
      const response = await apiRequest<Blob>(
        "GET",
        `api/filteration-results/${user_id}/download?${params}`,
         undefined,
        undefined,
        "stocks",
        undefined,
        "blob"
      );
      return response;
    },
    enabled: false,
  });

  useEffect(() => {
    if (!fileBlob) return;

    const url = window.URL.createObjectURL(fileBlob);
    const a = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `filteration_results_${user_id}_${timestamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [fileBlob]);

  useEffect(() => {
    setPage(0);
  }, [currentTradingMode]);




  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Filteration Results</h2>
          </div>

          <div className="overflow-x-auto">

           <DataTable
  title="Filters"
  headers={headers}
  data={
    results?.map((r: any) => [
      r.filter_name,
      r.action,
      r.timing,
      r.price,
      r.symbol,
    ]) || []
  }
  showSearch={false}
  onDownload={() => downloadFile()}
/>

          </div>

          <div className="flex justify-between pt-4">
            <Button disabled={page === 0 || isFetching} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span>Page {page + 1}</span>
            <Button
              disabled={(results?.length ?? 0) < limit || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}